/**
 * 弱密码校验工具
 * 严格按照指定的10条规则实现密码校验
 */

/**
 * 密码校验结果接口
 */
export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * 主要的密码校验函数
 * @param password - 待校验的密码
 * @param userName - 用户名，如果是邮箱格式则检查密码是否包含邮箱前缀
 * @returns 校验结果
 */
export function validatePassword(
  password: string,
  userName: string = ""
): PasswordValidationResult {
  const errors: string[] = [];

  // 规则1: 密码长度 >= 8
  if (!checkPasswordLength(password)) {
    errors.push("密码长度必须大于等于8位");
  }

  // 规则2: 必须包含数字，大小写字母，特殊符号
  if (!checkCharacterTypes(password)) {
    errors.push("密码必须包含数字、大小写字母和特殊符号");
  }

  // 规则3: 不能包含重复的字符 3次及以上
  if (!checkRepeatingCharacters(password)) {
    errors.push("密码不能包含重复的字符3次及以上");
  }

  // 规则4: 不能包含重复的段落(3~5位字符组成的段落)
  if (!checkRepeatingSegments(password)) {
    errors.push("密码不能包含重复的段落");
  }

  // 规则5: 不能包含连续的3位数字
  if (!checkConsecutiveNumbers(password)) {
    errors.push("密码不能包含连续的3位数字");
  }

  // 规则6: 不能包含4位及以上连续的字母
  if (!checkConsecutiveLetters(password)) {
    errors.push("密码不能包含4位及以上连续的字母");
  }

  // 规则7: 不能包含键盘上相邻的4位字符
  if (!checkKeyboardPatterns(password)) {
    errors.push("密码不能包含键盘上相邻的4位字符");
  }

  // 规则8: 不能包含禁用字符串
  if (!checkForbiddenStrings(password)) {
    errors.push("密码不能包含禁用的字符串");
  }

  // 规则9: 密码不能包含账号的邮箱前缀（仅当userName是邮箱格式时）
  if (
    userName &&
    userName.includes("@") &&
    !checkEmailPrefix(password, userName)
  ) {
    errors.push("密码不能包含邮箱前缀");
  }

  // 规则10: 不能用除了英文以外的其他 语言：中文、法文、意大利文、德文、俄文，等等
  if (!checkEnglishOnly(password)) {
    errors.push("密码只能包含英文字符、数字和符号");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * 规则1: 检查密码长度
 */
function checkPasswordLength(password: string): boolean {
  return password.length >= 8;
}

/**
 * 规则2: 检查字符类型 - 必须包含数字，大小写字母，特殊符号
 */
function checkCharacterTypes(password: string): boolean {
  const hasNumber = /\d/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasUpperCase = /[A-Z]/.test(password);
  // 至少包含一个非字母数字且非空白的字符（视为特殊符号，允许下划线作为特殊符号）
  const hasSpecialChar = /[^A-Za-z0-9\s]/.test(password);

  return hasNumber && hasLowerCase && hasUpperCase && hasSpecialChar;
}

/**
 * 规则3: 检查重复字符 - 不能包含重复的字符3次及以上(忽略大小写)
 */
function checkRepeatingCharacters(password: string): boolean {
  const lowerPassword = password.toLowerCase();
  for (let i = 0; i <= lowerPassword.length - 3; i++) {
    if (
      lowerPassword[i] === lowerPassword[i + 1] &&
      lowerPassword[i] === lowerPassword[i + 2]
    ) {
      return false;
    }
  }
  return true;
}

/**
 * 规则4: 检查重复段落 - 新的复合规则
 */
function checkRepeatingSegments(password: string): boolean {
  const lowerPassword = password.toLowerCase();

  // 4a: 2个及以上连续的2位字符组成的段落
  for (let i = 0; i <= lowerPassword.length - 4; i++) {
    const segment = lowerPassword.substring(i, i + 2);
    const nextSegment = lowerPassword.substring(i + 2, i + 4);
    if (segment === nextSegment) {
      return false;
    }
  }

  // 4b: 3个及以上非连续的2位字符组成的段落
  for (let i = 0; i <= lowerPassword.length - 2; i++) {
    const segment = lowerPassword.substring(i, i + 2);
    let count = 1;

    // 在后面的位置查找相同的2位段落
    for (let j = i + 2; j <= lowerPassword.length - 2; j++) {
      const compareSegment = lowerPassword.substring(j, j + 2);
      if (segment === compareSegment) {
        count++;
        if (count >= 3) {
          return false;
        }
      }
    }
  }

  // 4c: 2个及以上3~5位字符组成的段落
  for (let segmentLength = 3; segmentLength <= 5; segmentLength++) {
    for (let i = 0; i <= lowerPassword.length - segmentLength; i++) {
      const segment = lowerPassword.substring(i, i + segmentLength);
      const remainingPassword = lowerPassword.substring(i + segmentLength);
      // 在密码的其余部分查找相同的段落
      if (remainingPassword.includes(segment)) {
        return false;
      }
    }
  }

  return true;
}

/**
 * 规则5: 检查连续数字 - 不能包含连续的3位数字
 */
function checkConsecutiveNumbers(password: string): boolean {
  const consecutiveNumbers = [
    "012",
    "123",
    "234",
    "345",
    "456",
    "567",
    "678",
    "789",
  ];

  for (const pattern of consecutiveNumbers) {
    if (password.includes(pattern)) {
      return false;
    }
  }
  return true;
}

/**
 * 规则6: 检查连续字母 - 不能包含4位及以上连续的字母(忽略大小写)
 */
function checkConsecutiveLetters(password: string): boolean {
  const lowerPassword = password.toLowerCase();

  // 检查4位及以上的连续字母
  for (let i = 0; i <= lowerPassword.length - 4; i++) {
    let isConsecutive = true;

    // 检查从当前位置开始的4个字符是否连续
    for (let j = 1; j < 4; j++) {
      const currentChar = lowerPassword.charCodeAt(i + j - 1);
      const nextChar = lowerPassword.charCodeAt(i + j);

      // 检查是否是字母且连续
      if (
        currentChar < 97 ||
        currentChar > 122 || // 不是小写字母
        nextChar < 97 ||
        nextChar > 122 || // 不是小写字母
        nextChar !== currentChar + 1
      ) {
        // 不连续
        isConsecutive = false;
        break;
      }
    }

    if (isConsecutive) {
      return false;
    }
  }

  return true;
}

/**
 * 规则7: 检查键盘相邻字符 - 不能包含键盘上相邻的4位字符
 */
function checkKeyboardPatterns(password: string): boolean {
  // 键盘布局定义 - 按照键盘每一行的实际布局
  const keyboardRows = [
    "1234567890-=",
    "qwertyuiop[]\\",
    "asdfghjkl;'",
    "zxcvbnm,./",
    "!@#$%^&*()_+",
    "QWERTYUIOP{}|",
    'ASDFGHJKL:"',
    "ZXCVBNM<>?",
  ];

  const lowerPassword = password.toLowerCase();

  // 检查每一行的连续4个字符
  for (const row of keyboardRows) {
    const lowerRow = row.toLowerCase();
    for (let i = 0; i <= lowerRow.length - 4; i++) {
      const pattern = lowerRow.substring(i, i + 4);
      if (lowerPassword.includes(pattern)) {
        return false;
      }
    }
  }

  return true;
}

/**
 * 规则8: 检查禁用字符串 - 不能包含指定的字符串（忽略大小写）
 */
function checkForbiddenStrings(password: string): boolean {
  const forbiddenStrings = [
    "kenc",
    "kingsoft",
    "jinshan",
    "ksyun",
    "admin",
    "password",
    "hello",

    "super123123!",
    "super12311!",
    "super1231!",
    "QF12345!",
    "as@#$%369DF",
    "q1w2e3r4!",
    "p@ssw0rd",
    "a123456!",
    "1qaz@WSX",
    "yy7752!!",
    "123qwe!",
    "abc123!",
    "july2801!",
    "1qaz!QAZ",
    "woaini1314!",
    "pa$$w0rd",
    "!QAZ2wsx",
    "P@$$w0rd",
    "zaq1@WSX",
    "ZAQ!2wsx",
    "!QAZ1qaz",
    "P@55w0rd",
    "1qazZAQ!",
    "!QAZxsw2",
    "1qazXSW@",
    "pass@123",
    "Password1!",
    "P@ssw0rd1",
    "password@1",
    "P@ssword1",
    "password@123",
    "123qwe!@#",
    "p@55word",
    "Abc123456!",
    "!Turbine1",
    "fxzZ75$yer",
    "Temp2014!",
    "Comply1!",
    "L58jkdjP!m",
    "sw3434!",
    "4&use24HyyJ",
    "Paperindex1*",
    "abc@123",
    "aysq1984#",
    "sat99#dx",
    "gm718422@",
    "qwe123!",
    "Sierra#123",
    "123abc!",
    "v123456*",
    "g00dPa$$w0rD",
    "admin@123",
    "Jammu@123",
    "Welcome1!",
    "asd123!",
    "abc123#",
    "qwerty1!",
    "abcd@1234",
    "zaq1ZAQ!",
    "Passw0rd!",
    "pacific@123",
    "TtAt4pu71&N",
    "abcd1234!",
    "india@123",
    "abc123!@#",
    "p@cific123",
    "Mh@270481",
    "a!515253",
    "zaq1!QAZ",
    "pass@word1",
    "Abc@#123",
    "aicte@123",
    "!QAZzaq1",
    "Asdfasdf1!",
    "ZAQ!xsw2",
    "ZAQ!1qaz",
    "c123456*",
    "Hello123!",
    "1q2w3e4r!",
    "123@abc",
    "123456a@",
    "!54321qw",
    "welcome@123",
    "abc123@",
    "!Q2w3e4r",
    "qwerty@123",
    "qwerty123!",
    "test@123",
    "Ebels!23!",
    "1234qwer!",
    "123456a!",
    "*svcskl22",
    "Password123!",
    "Admin123!",
    "wipro@123",
    "1qaz2wsx!",
    "hugsalot*22",
    "12qwaszx!",
    "ZAQ!zaq1",
    "DADWAL@007",
    "password!23",
    "Abc@12345",
    "1q2w!Q",
    "sitdu14A@",
    "786786Abc@",
    "123abc!!!",
    "Qwer!234",
    "asd@123",
    "qwer1234!",
    "123asd!",
    "Dell1599!",
    "Password2@",
    "zou0o!QE",
    "$$$$$$n135",
    "123@123a",
    "Is9w6&r1eWB",
    "test123!",
    "P@ssw0rd123",
    "qwe123!@#",
    "a168898aBc*",
    "am4377693*",
    "2wsx@WSX",
    "12qw!@QW",
    "1qaz2wsx!QAZ",
    "asdf1234!",
    "abc123!!",
    "pass@1234",
    "trustno1!",
    "Zx!2cv34",
    "@Gmail123",
    "Abcd!234",
    "Aicte@2017",
    "Ziruuff99@",
    "password#1",
    "abc123*",
    "123qweasd!",
    "jesus#1",
    "0502123*toy",
    "digua520!",
    "oup3Xt&3i1R",
    "P@ssword123",
    "password!1",
    "grimt@2008",
    "Macbook1!",
    "1q2w3e!Q",
    "kathua@123",
    "abc@1234",
    "hello@123",
    "1q2w3e!",
    "RAND#a#8",
    "123456@a",
    "151210asD!",
    "Kashmir@123",
    "1qaz2wsx@",
    "asd123!@#",
    "abcd@123",
    "Pa55w0rd!",
    "aaAA11!!",
    "asdf@1234",
    "chivas#1",
    "gj7b!x",
    "bill!98aa",
    "xbox360!",
    "abhi@123",
    "dragon1!",
    "blink182!",
    "pakistan123*",
    "!234Qwer",
    "#1bitch",
    "Sopore@123",
    "aaa111!!!",
    "123@CSCz",
    "amit@123",
    "data@123",
    "123@qwe",
    "TRAL@123",
    "#1stunna",
    "Superman1!",
    "monkey1!",
    "Q!w2e3r4",
    "1234@abcd",
    "Success100%",
    "admin!2010",
    "virus@91",
    "abcd123!",
    "Qq123123??",
    "@lthebest1",
    "Jesuit86!",
    "Lvr&esa158C",
    "love@123",
    "welcome123*",
    "charlie1!",
    "1q2w3e$R",
    "Letmein1!",
    "123qwe!@#QWE",
    "ZAchary01!!",
    "1qay!QAY",
    "Pa55word!",
    "P@ssw0rd!",
    "memo#555",
    "sXjq82jo!6L",
    "123!@#qwe",
    "1q2w#E$R",
    "Apple@1500",
    "abc123$",
    "fuckyou2!",
    "test1234!",
    "1q2w3e4r5t!",
    "a5Q&8z1bjhO",
    "princess#1",
    "1qaz@WSX3edc",
    "jesusis#1",
    "prince1!",
    "Password01!",
    "p4$$w0rd",
    "pakistan@123",
    "candy2009**",
    "qaz123!",
    "Football1!",
    "Padakar88!",
    "123@intel",
    "Pass123!",
    "Welcome@1",
    "abc123!!!",
    "david12!",
    "q1w2e3!",
    "rahul@123",
    "fuck0ff!",
    "1q2w3e4r!Q",
    "Bluestar@123",
    "iloveyou2!",
    "Asdf!234",
    "Sunshine1!",
    "apple@123",
    "asdf123!",
    "123abc!@#",
    "Thought01!",
    "w0rds@123",
    "!QAY2wsx",
    "Digua520!!",
    "Video11!",
    "wai1in123!",
    "infy@123",
    "Ih7lrpK5&2K",
    "asdf@123",
    "number1!",
    "qwe123QWE!",
    "sexy#1",
    "1234abcd!",
    "1qw2!QW",
    "Baseball1!",
    "qwer@1234",
    "Fahkit9!",
    "Usa@2011",
    "admin@1234",
    "michael1!",
    "pa$$word1",
    "ajay@123",
    "derp12!@",
    "ek22512!",
    "p4ssw0rd!",
    "reset@123",
    "uh97&tQl6uP",
    "welcome@12",
    "7ujm&UJM",
    "Hello!123",
    "b141548*",
    "bla123KMR@",
    "Xyz@12345",
    "sco4phi!",
    "!Q@W3e4r",
    "Aug!272010",
    "Welcome123!",
    "ABCabc123!",
    "Sunil@123",
    "pass@12345",
    "Password$1",
    "bandit1!",
    "fuckyou1!",
    "jarox1301!",
    "kumar@123",
    "qwerty12!",
    "ravi@123",
    "singh@123",
    "123abc@",
    "4rfv$RFV",
    "Bablo@123",
    "zxc123!",
    "#1princess",
    "Change@123",
    "Great!11",
    "soccer#1",
    "!victor!1",
    "makara1!",
    "temp@123",
    "3edc#EDC",
    "Qazigund@1",
    "Wai!in123",
    "Sp!tfire123",
    "bolpuR@1",
    "qwerty!1",
    "!Q2w3e4r5t",
    "computer1!",
    "country1!",
    "qazwsx123!",
    "rangers#1",
    "change#123",
    "dSjr!y5jh",
    "f00tb@ll",
    "raiders#1",
    "123456q!",
    "1qazxsw2!",
    "Direct456!",
    "Lra12413!",
    "b123456*",
    "pokemon1!",
    "#1pimp",
    "5tgb%TGB",
    "Shadow1!",
    "qwe123!!",
    "100%cool",
    "123456qwe!",
    "zaq1XSW@",
    "!@#123qwe",
    "!password1",
    "Yankees1!",
    "a@123456",
    "iloveyou1!",
    "pass123$",
    "xsw2!QAZ",
    "123456aA@",
    "Falak@123",
    "Shivalik@123",
    "babygirl#1",
    "qazwsx1!",
    "Prep@1234",
    "Vishal@123",
    "joshua1!",
    "123!@#qweQWE",
    "Modern@121",
    "Snowball1!",
    "hunter1!",
    "krishna@123",
    "mes!!!346",
    "123456789a@",
    "sp!d3rh!v3",
    "100%jesus",
    "1q2w3e!Q@W#E",
    "227222Ww!",
    "Jessica1!",
    "Kathua@12345",
    "Password12!",
    "cKrazy123!",
    "1Password!",
    "Allah@786",
    "ENergy85!!",
    "Jordan1!",
    "Jordan23!",
    "Xenacool!1",
    "lna7nrj!",
    "sachin@123",
    "soccer1!",
    "1q2w!Q@W",
    "Qwerty!2",
    "Rajouri123@",
    "Sovkom!1975",
    "aman@123",
    "cowboys#1",
    "lucky#13",
    "q1w2Q!W",
    "1q2w3e4r!!",
    "Acc1234$$",
    "Freedom1!",
    "Liverpool1!",
    "Shahid@123",
    "abc1234!",
    "pankaj@123",
    "pass1234!",
    "123!@#abc",
    "12qwas!",
    "Littleman01!",
    "Sahil@123",
    "abc!123",
    "abcd123$",
    "abcd1234$",
    "ghost1022!",
    "love123!",
    "raj@123",
    "*w4k3UPn3o!",
    "123qwe!!",
    "GHJK!248",
    "Welcome!1",
    "alfa@123",
    "qweQWE123!",
    "123456!a",
    "123654789a!",
    "Minecraft1!",
    "Net@1234",
    "sarvam@310",
    "#1angel",
    "2wsx!QAZ",
    "3edc$RFV",
    "Jasmine1!",
    "Newuser@123",
    "a12345!",
    "aB!cDo18P",
    "dev2012@",
    "fri$$1205",
    "killer123!",
    "master1!",
    "the1&only",
    "!QA2ws3ed",
    "a1a1a1A!",
    "abcxyz@123",
    "gateways9#",
    "hottie#1",
    "l3tm31n!",
    "password1$",
    "tn20110801!",
    "united@1",
    "Allah@123",
    "D41d8cd!",
    "DeadEye1@2",
    "Ramban@123",
    "Suhail@123",
    "abc123**",
    "fr33dom!",
    "hello1!",
    "lakers#1",
    "p455w0rd!",
    "password@12",
    "#1player",
    "100%sexy",
    "12345678a!",
    "123@abcd",
    "Matthew1!",
    "Qwerty!23",
    "a1b2c3d4!",
    "hua@5817",
    "skate0n!",
    "zxcvbnm1!",
    "!QA2ws",
    "123zxc!",
    "Blessed1!",
    "Core2016!",
    "Cw$z6q3f4fH",
    "Naruto123@",
    "Summer1!",
    "Xddxdd12!",
    "ccare12!",
    "1q@W3e$R",
    "Cl1010779!",
    "G6rf$$5rt",
    "Muskan@123",
    "g6yf$$stt",
    "qwerty@12345",
    "redsox#1",
    "000webhost!",
    "100%love",
    "1qaz!QAZ2wsx",
    "Google@123",
    "abc123***",
    "angel#1",
    "asdasd1!",
    "asdqwe123!",
    "ashish@123",
    "bearbear21!",
    "deepak@123",
    "dr@g0n",
    "killer1!",
    "1q2w3e4r&",
    "2wsx#EDC",
    "50cent!",
    "Airborne1!",
    "Nicholas1!",
    "P@55w0rd!",
    "Waseem@123",
    "bram1414!",
    "cutie#1",
    "myspace1!",
    "123456?a",
    "Madison1!",
    "ali123!",
    "buster1!",
    "iMac@0910",
    "lol123!",
    "parool14?",
    "str00ck!S",
    "test@1234",
    "Apple123!",
    "Dosses12!",
    "Marines1!",
    "Qwerty12345!",
    "Sharma@123",
    "Start123!",
    "abc@123456",
    "lucky13!",
    "nokia@123",
    "qqQQ11!!",
    "qwQW12!",
    "Hopkins96!",
    "Princess1!",
    "login@123",
    "qqQQ11!!",
    "qw12QW!",
    "qwe@123",
    "qwerty123@",
    "steelers#1",
    "vc111!!!",
    "xx0jsk!3sk",
    "zxasqw12!",
    "!QAZ2wsx3edc",
    "123456789a!",
    "123qwe&",
    "Inuyasha1!",
    "Online@123",
    "Patrick1!",
    "a6d!3ll3",
    "dell@123",
    "ganesh@123",
    "ichigo100%",
    "lucky#7",
    "master@123",
    "oicu812!",
    "!123456a",
    "Chicken1!",
    "Danish@123",
    "G@briel123",
    "Kk44298104!",
    "P@ssword01",
    "SwaGo!1",
    "abhishek@123",
    "akshay@123",
    "asd@1234",
    "card@1234",
    "football#1",
    "password*1",
    "samsung1!",
    "samsung@123",
    "sunny@123",
    "user@123",
    "vikas@123",
    "0000qwe!",
    "12345qwert!",
    "123abc*",
    "Andrew1!",
    "Jennifer1!",
    "Orange1!",
    "Passw0rd1!",
    "Soccer12!",
    "annie23!",
    "asdfgh1!",
    "gaurav@123",
    "hallo123!",
    "hsaa#007",
    "john@123",
    "manish@123",
    "rangers1!",
    "sonu@123",
    "stef#1",
    "!B1c9af37",
    "1234@qwer",
    "@wsx3edc",
    "Crystal1!",
    "JAHjah2288#",
    "Monkey123!",
    "Something1!",
    "fake@1234",
    "fwt@$$jj4d",
    "mnbvcxz1!",
    "naruto1!",
    "password@2",
    "player66!",
    "qwer123!",
    "tata@123",
    "zyte12!!",
    "!4zrEza1n7P",
    "#1love",
    "#@!321ewq",
    "100%bitch",
    "4ever&ever",
    "@slab3",
    "Blogger1!",
    "Letme1n!",
    "Paki045*",
    "Pepsicola1!",
    "Srinagar@786",
    "Zion151210!",
    "abc*123",
    "anil@123",
    "blue123!",
    "family#1",
    "godis#1",
    "qweasd123!",
    "qwerty@1",
    "rajesh@123",
    "!QAZ3edc",
    "#1hottie",
    "#EDC3edc",
    "123abc!!",
    "Akash@123",
    "Fw@rtyb2d",
    "a1b2c3!",
    "iamgay33!",
    "logite123!",
    "mexico#1",
    "pass#123",
    "pooja@123",
    "qwerty1234!",
    "secure@123",
    "vijay@123",
    "15Sn0wba!!82",
    "1a2b3c4d!",
    "@WSX2wsx",
    "Aditya@123",
    "Alexander1!",
    "Diamond1!",
    "Direct17!",
    "Pa$$w0rd1",
    "Prince@786",
    "Sample!123",
    "Starwars1!",
    "Thisisgay1!",
    "Ummer@123",
    "cheese1!",
    "n4003070***",
    "passwd@123",
    "whatever1!",
    "12qwasyx!",
    "1q1q1q!Q",
    "Airforce1!",
    "Bla@12345",
    "Chester1!",
    "Cooper1276!",
    "Lahore@123",
    "Pinkfloyd1!",
    "Rohit@123",
    "abcd1234@",
    "caddy&22",
    "changeme!1",
    "dani669!!",
    "india@1234",
    "justin#1",
    "lily123!",
    "love@143",
    "priya@123",
    "qazwsx123@",
    "sanjay@123",
    "vdch@123",
    "!QAZ2wsx#EDC",
    "#1lover",
    "%E2%82%AC",
    "123456a*",
    "123@com",
    "123qaz!",
    "3xchange!",
    "Aadil@123",
    "Basketball1!",
    "Darpan@@7",
    "airtel@123",
    "ashu@123",
    "blood43!",
    "computer@123",
    "monkey#1",
    "password123$",
    "q1w2e3R$",
    "satyam123$",
    "seo@12345",
    "smadan90!",
    "1q2w3e!!",
    "Jackson1!",
    "Kxrider052!",
    "Monster1!",
    "P@ss1234",
    "Password1@",
    "asd123!!",
    "hulk@150",
    "kiran@123",
    "limpbizkit1!",
    "pass123#",
    "q1w2e3!!",
    "scotland1!",
    "skyking009*",
    "12345a@",
    "1234qwe!",
    "1234qwer$",
    "123qwerty!",
    "1million$",
    "1q2w3e4r@",
    "521baobei!!!",
    "Homestar007!",
    "Mmmm!111",
    "P4ssword!",
    "Steelers1!",
    "T4u!p1eww4P",
    "Thunder1!",
    "William1!",
    "abcd123@",
    "abcd@12345",
    "company@123",
    "gators#1",
    "guitar1!",
    "khan@123",
    "manoj@123",
    "p0pmagicwd**",
    "prince@123",
    "qwerty123$",
    "suresh@123",
    "tmr777!!",
    "wqazx4rry0n!",
    "zxcvbnm@123Z",
    "Chocolate1!",
    "M0nster!",
    "P@$$w0rd1",
    "PA$$word01",
    "Password1*",
    "abacus1!",
    "anu17488@",
    "chris12345!",
    "compaq1!",
    "fucky0u!",
    "love#1",
    "nitin@123",
    "qwe123QWE!@#",
    "qwert123!",
    "star@123",
    "yankees#1",
    "zaq12wsx!",
    "#1family",
    "#EDC4rfv",
    "!@#$qwerasdf",
    "$RFV1qaz",
    "$RFV2wsx",
    "$RFV3edc",
    "$rfv4RFV",
    "=[;.0okm",
    "0okm=[;.",
    "0okm-pl,",
    "0p;/1qaz",
    "0p;/2wsx",
    "0p;/3edc",
    "0p;/4rfv",
    "0p;/5tgb",
    "0p;/6yhn",
    "0p;/7ujm",
    "0p;/8ik,",
    "0p;/9ol.",
    "0p;/)P:?",
    "123!@#",
    "123456789a",
    "123456a",
    "123456y",
    "123456yu",
    "1234abcd",
    "1234.com",
    "1234qwer",
    "1234qwer!@#$",
    "1234qwerasdf",
    "1234zxcv",
    "123qwe",
    "12qwas!@",
    "12qwaszx",
    "1q1q1q",
    "1q1q1q1q",
    "1q2w3e",
    "1q2w3e4",
    "1q2w3e4r",
    "1q2w3e4r$R#E@W!Q",
    "1q2w3e4r5t",
    "1q2w3e4r5t6y",
    "1q2w3e4r!Q@W#E$R",
    "1q2w3e#E@W!Q",
    "1qaz!@#$",
    "1qaz$RFV",
    "1qaz0p;/",
    "1qaz1qaz",
    "1!qaz2@wsx",
    "1qaz2wsx",
    "1qaz2wsx!@",
    "1qaz2wsx@!",
    "1qaz2wsx1234",
    "1qaz2wsx3edc",
    "1qaz2wsx3edc!@",
    "1qaz2wsx3edc!@#",
    "1qaz2wsx3edc@!",
    "1qaz3edc",
    "1qaz4rfv",
    "1qaz5tgb",
    "1qaz6yhn",
    "1qaz7ujm",
    "1qaz8ik,",
    "1qaz9ol.",
    "1qaz#EDC",
    "1qaz*IK<",
    "1qaz(OL>",
    "1qaz)P:?",
    "1qaz%TGB",
    "1qaz&UJM",
    "1qaz@WSX#EDC",
    "1qaz@XSW",
    "1qazxsw2",
    "1qaz^YHN",
    "1qw2!QW@",
    "1qw2@WQ!",
    "1q@W3e",
    "2wsx$RFV",
    "2wsx0p;/",
    "2wsx1qaz",
    "2wsx1qaz!@",
    "2wsx1qaz@!",
    "2wsx2wsx",
    "2wsx3edc",
    "2wsx4rfv",
    "2wsx5tgb",
    "2wsx6yhn",
    "2wsx7ujm",
    "2wsx8ik,",
    "2wsx9ol.",
    "2wsx*IK<",
    "2wsx(OL>)",
    "2wsx)P:?",
    "2wsx%TGB",
    "2wsx&UJM",
    "2wsx^YHN",
    "3e2w1q#E@W!",
    "3edc0p;/",
    "3edc1qaz",
    "3edc2wsx",
    "3edc2wsx1qaz",
    "3edc2wsx1qaz!@",
    "3edc2wsx1qaz@!",
    "3edc4rfv",
    "3edc5tgb",
    "3edc6yhn",
    "3edc7ujm",
    "3edc8ik,",
    "3edc9ol.",
    "3edc*IK<",
    "3edc(OL>)",
    "3edc)P:?",
    "3edc%TGB",
    "3edc&UJM",
    "3edc@WSX1qaz",
    "3edc@WSX1qaz!@",
    "3edc^YHN",
    "=[;.4esz",
    "4esz=[;.",
    "4esz-pl,",
    "4r3e2w1q",
    "4r3e2w1q$R#E@W!Q",
    "4r3e2w1q!Q@W#E$R",
    "4rfv0p;/",
    "4rfv1qaz",
    "4rfv2wsx",
    "4rfv3edc",
    "4rfv4rfv",
    "4rfv5tgb",
    "4rfv6yhn",
    "4rfv7ujm",
    "4rfv8ik,",
    "4rfv9ol.",
    "4rfv#EDC",
    "4rfv*IK<",
    "4rfv(OL>)",
    "4rfv)P:?",
    "4rfv%TGB",
    "4rfv&UJM",
    "4rfv^YHN",
    "=[;.5rdx",
    "5rdx=[;.",
    "5rdx-pl,",
    "5tgb0p;/",
    "5tgb1qaz",
    "5tgb2wsx",
    "5tgb3edc",
    "5tgb4rfv",
    "5tgb5tgb",
    "5tgb6yhn",
    "5tgb7ujm",
    "5tgb8ik,",
    "5tgb9ol.",
    "5tgb*IK<",
    "5tgb(OL>)",
    "5tgb)P:?",
    "5tgb&UJM",
    "5tgb^YHN",
    "=[;.6tfc",
    "6tfc=[;.",
    "6tfc-pl,",
    "6yhn0p;/",
    "6yhn1qaz",
    "6yhn2wsx",
    "6yhn3edc",
    "6yhn4rfv",
    "6yhn5tgb",
    "6yhn6yhn",
    "6yhn7ujm",
    "6yhn8ik,",
    "6yhn9ol.",
    "6yhn*IK<",
    "6yhn(OL>)",
    "6yhn)P:?",
    "6yhn%TGB",
    "6yhn&UJM",
    "6YHN&ujv",
    "6yhn^YHN",
    "7ujm0p;/",
    "7ujm1qaz",
    "7ujm2wsx",
    "7ujm3edc",
    "7ujm4rfv",
    "7ujm5tgb",
    "7ujm6yhn",
    "7ujm7ujm",
    "7ujm8ik,",
    "7ujm9ol.",
    "7ujm*IK<",
    "7ujm(OL>)",
    "7ujm)P:?",
    "7ujm^YHN",
    "=[;.7ygv",
    "7ygv=[;.",
    "7ygv-pl,",
    "8ik,0p;/",
    "8ik,1qaz",
    "8ik,2wsx",
    "8ik,3edc",
    "8ik,4rfv",
    "8ik,5tgb",
    "8ik,6yhn",
    "8ik,7ujm",
    "8ik,8ik,",
    "8ik,9ol.",
    "8ik,*IK<",
    "8ik,(OL>)",
    "8ik,)P:?",
    "=[;.8uhb",
    "8uhb=[;.",
    "8uhb-pl,",
    "=[;.9ijn",
    "9ijn=[;.",
    "9ijn-pl,",
    "9ol.0p;/",
    "9ol.1qaz",
    "9ol.2wsx",
    "9ol.3edc",
    "9ol.4rfv",
    "9ol.5tgb",
    "9ol.6yhn",
    "9ol.7ujm",
    "9ol.8ik,",
    "9ol.9ol.",
    "9ol.(OL>)",
    "9ol.)P:?",
    "a123456",
    "a123456789",
    "aa123456",
    "Aa123456.",
    "Aa12345678",
    "abc123",
    "abcABC123",
    "abcd1234",
    "admin123",
    "admin1qaz2wsx",
    "admin888",
    "asdfghjkl;'",
    "@bcd1234",
    "#EDC1qaz",
    "#EDC2wsx",
    "ewq123456",
    "Huawei@123",
    "*IK<1qaz",
    "*IK<2wsx",
    "*IK<3edc",
    "*IK<4rfv",
    "*IK<5tgb",
    "*IK<6yhn",
    "*IK<7ujm",
    "*IK<8ik,",
    "*IK<9ol.",
    "Liyan!2#4%6",
    "';lkjhgfdsa",
    "/.,mnbvcxz",
    "(OL>1qaz",
    "(OL>2wsx",
    "(OL>3edc",
    "(OL>4rfv",
    "(OL>5tgb",
    "(OL>6yhn",
    "(OL>7ujm",
    "(OL>8ik,",
    "(OL>9ol.",
    "P@$$word",
    "P@$$word123",
    ")P:?0p;/",
    ")P:?1qaz",
    ")P:?2wsx",
    ")P:?3edc",
    ")P:?4rfv",
    "p4ssw0rd",
    "p4ssw0rd1",
    ")P:?5tgb",
    ")P:?6yhn",
    ")P:?7ujm",
    ")P:?8ik,",
    ")P:?9ol.",
    "passw0rd",
    "Password01",
    "password1",
    "Password123",
    "-pl,=[;.",
    "=[;.-pl,",
    "-pl,0okm",
    "-pl,4esz",
    "-pl,5rdx",
    "-pl,6tfc",
    "-pl,7ygv",
    "-pl,8uhb",
    "-pl,9ijn",
    "\\][poiuytrewq",
    "p@ssword",
    "P@w0rd",
    "P@word",
    "q12345",
    "q123456",
    "q1234567",
    "q12345678",
    "q1q1q1",
    "q1q1q1q",
    "q1q1q1q1",
    "q1w2e3",
    "q1w2e3r4",
    "q1w2e3r4Q!W@E#R$",
    "q1w2e3r4R$E#W@Q!",
    "q1w2e3r4t5",
    "!Q2w#E4r",
    "!QAZ2wsx@",
    "qazWSX^&*",
    "!QAZwsx",
    "QAZwsx123",
    "!QAZ@WSX#EDC",
    "qazxsw123",
    "Qiyi@123",
    "Qiyi@123456",
    "Qiyi@124",
    "qw123456",
    "!QW@1qw2",
    "Qwaszx12",
    "!Q@W#E$R1q2w3e4r",
    "!Q@W#E$R4r3e2w1q",
    "qwe123",
    "qwe1234",
    "qwe1234a",
    "qwe321",
    "!Q@W#EE$R",
    "Q!W@E#R$",
    "QWER!@#$",
    "Q!W@E#R$q1w2e3r4",
    "Q!W@E#R$r4e3w2q1",
    "qwer1234!@#$",
    "qwer12345",
    "qwerasdf",
    "qwerasdf!@#$",
    "qwerasdf1234",
    "qwertyuiop[]",
    "qwertyuiop[]\\",
    "qwqwqw123",
    "R$E#W@Q!q1w2e3r4",
    "R$E#W@Q!r4e3w2q1",
    "r4e3w2q1",
    "r4e3w2q1Q!W@E#R$",
    "r4e3w2q1R$E#W@Q!",
    "sa2008",
    "sql2005",
    "sql2008",
    "test123",
    "%TGB1qaz",
    "%TGB2wsx",
    "%TGB3edc",
    "%TGB4rfv",
    "%TGB5tgb",
    "%TGB6yhn",
    "&UJM1qaz",
    "&UJM2wsx",
    "&UJM3edc",
    "&UJM4rfv",
    "&UJM5tgb",
    "&UJM6yhn",
    "&UJM7ujm",
    "w123456",
    "w1234567",
    "w12345678",
    "w2w2w2",
    "wq123456",
    "@WQ!2wq1",
    "@WSX1qaz",
    "^YHN1qaz",
    "^YHN2wsx",
    "^YHN3edc",
    "^YHN4rfv",
    "^YHN5tgb",
    "^YHN6yhn",
    "^YHN7ujm",
    "zaq123",
    "zaq12wsx",
    "zaq1bgt5",
    "zaq1cde3",
    "zaq1,ki8",
    "zaq1.lo9",
    "zaq1mju7",
    "zaq1nhy6",
    "zaq1vfr4",
    "zaq1xsw2",
    "zaq1xsw2!@",
    "zaq1xsw2@!",
    "zaq1xsw2cde3!@",
    "zaq1xsw2cde3!@#",
    "zaq1xsw2cde3#@!",
    "zaq1xsw2cde3@!",
    "zxcvbnm,./",
  ];

  const lowerPassword = password.toLowerCase();

  for (const forbidden of forbiddenStrings) {
    if (lowerPassword.includes(forbidden.toLowerCase())) {
      return false;
    }
  }
  return true;
}

/**
 * 规则9: 检查邮箱前缀 - 密码不能包含账号的邮箱前缀
 */
function checkEmailPrefix(password: string, email: string): boolean {
  const emailPrefix = email.split("@")[0].toLowerCase();
  if (!emailPrefix) {
    return true;
  }
  const lowerPassword = password.toLowerCase();

  return !lowerPassword.includes(emailPrefix);
}

 

/**
 * 规则10: 检查是否只包含英文字符 - 不能包含中文、法文、意大利文、德文、俄文等其他语言字符
 */
function checkEnglishOnly(password: string): boolean {
  // 使用逐字符检查以避免在源代码中出现控制字符转义
  // 允许的范围为所有ASCII字符（码点0x00到0x7F），如果遇到任何大于0x7F的字符则返回false
  for (let i = 0; i < password.length; i++) {
    if (password.charCodeAt(i) > 0x7f) {
      return false
    }
  }
  return true
}
